/**
 * A floating label for controls drawn on a canvas (the pause gear, chess's
 * move-list toggle), which have no DOM element of their own to carry a
 * `title`. The game does its own hit-testing and calls `set(text)` whenever
 * the hovered control changes; the label follows the pointer, so no game has
 * to map its canvas coordinates — rotated or scaled as they may be — to the
 * page.
 */
import { el } from "./components/dom";

/** Gap between the pointer and the label, in CSS px (it trails a moving pointer). */
const POINTER_OFFSET_PX = 14;

export class CanvasTooltip {
  private readonly node = el("div", { class: "canvas-tooltip", attrs: { role: "tooltip" } });
  private text: string | null = null;
  private x = 0;
  private y = 0;

  private readonly onMove = (e: PointerEvent): void => {
    this.x = e.clientX;
    this.y = e.clientY;
    if (this.text !== null) this.place();
  };
  private readonly onLeave = (): void => this.set(null);
  // A key press can change the screen under a resting pointer (Escape opens the
  // pause menu over the gear): drop the label; the next pointer move restores it.
  private readonly onKeyDown = (): void => this.set(null);

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.node.hidden = true;
    document.body.appendChild(this.node);
    canvas.addEventListener("pointermove", this.onMove);
    canvas.addEventListener("pointerdown", this.onMove);
    canvas.addEventListener("pointerleave", this.onLeave);
    window.addEventListener("keydown", this.onKeyDown);
  }

  /** Show `text` by the pointer, or hide the label with null. Cheap to call on every pointer event. */
  set(text: string | null): void {
    if (text === this.text) return;
    this.text = text;
    if (text === null) {
      this.node.hidden = true;
      return;
    }
    this.node.textContent = text;
    this.node.hidden = false;
    this.place();
  }

  dispose(): void {
    this.canvas.removeEventListener("pointermove", this.onMove);
    this.canvas.removeEventListener("pointerdown", this.onMove);
    this.canvas.removeEventListener("pointerleave", this.onLeave);
    window.removeEventListener("keydown", this.onKeyDown);
    this.node.remove();
  }

  /** Below the pointer, growing away from the nearer side edge so it never leaves the viewport. */
  private place(): void {
    const style = this.node.style;
    style.top = `${this.y + POINTER_OFFSET_PX}px`;
    if (this.x > window.innerWidth / 2) {
      style.left = "";
      style.right = `${window.innerWidth - this.x}px`;
    } else {
      style.right = "";
      style.left = `${this.x + POINTER_OFFSET_PX}px`;
    }
  }
}
